import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MessageCircleMore,
  Zap,
  AppWindow,
  LayoutDashboard,
  MessageSquareText,
  Sparkles,
  TrendingUp,
  Users,
  Clock,
  Send,
  Rocket,
  LogIn,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast, Toaster } from "sonner";
import { api } from "../lib/api";
import { GenderModal } from "../components/GenderModal";
import { readGender } from "../lib/gender";

const services = [
  {
    category: "ניהול דשבורד כללי",
    icon: LayoutDashboard,
    color: "from-pink-50 to-fuchsia-50",
    iconBg: "bg-pink-600",
    features: [
      { name: "ניהול מלא של העסק" },
      { name: "ללא ספקי צד ג׳" },
      { name: "אוטומציה פנימית של דוחות" },
      { name: "מותאם אישית לעסק שלך" },
    ],
    testid: "service-dashboard",
  },
  {
    category: "שירותי וואטסאפ",
    icon: MessageCircleMore,
    color: "from-green-50 to-emerald-50",
    iconBg: "bg-whatsapp",
    features: [
      { name: "שליחת חשבוניות אוטומטית" },
      { name: "תזכורות תשלום מתוזמנות" },
      { name: "מסרונים לקמפיינים" },
      { name: "בוט שירות לקוחות" },
    ],
    testid: "service-whatsapp",
  },
  {
    category: "שירותי SMS",
    icon: MessageSquareText,
    color: "from-sky-50 to-blue-50",
    iconBg: "bg-sky-600",
    features: [
      { name: "הודעות תזכורת ללקוחות" },
      { name: "אישורי הגעה / תורים" },
      { name: "קמפיינים מבוססי SMS" },
      { name: "OTP / אימותים" },
    ],
    testid: "service-sms",
  },
  {
    category: "אוטומציות לעסק",
    icon: Zap,
    color: "from-brand-50 to-teal-50",
    iconBg: "bg-brand",
    features: [
      { name: "חיבור ל-Google Sheets" },
      { name: "הפקת מסמכים מחשבונית ירוקה" },
      { name: "אוטומציה של גביה" },
      { name: "ניהול משימות חוזרות" },
    ],
    testid: "service-automations",
  },
  {
    category: "אפליקציות לעסק",
    icon: AppWindow,
    color: "from-blue-50 to-sky-50",
    iconBg: "bg-sky-600",
    features: [
      { name: "CRM חכם" },
      { name: "ניהול לקוחות ומועדים" },
      { name: "דוחות ותובנות" },
      { name: "אפליקציה ייעודית לעסק שלך" },
    ],
    testid: "service-apps",
  },
];

const stats = [
  { icon: TrendingUp, label: "חסכון בזמן", value: "90%" },
  { icon: Users, label: "לקוחות פעילים", value: "100+" },
  { icon: Clock, label: "שליחה אוטומטית", value: "24/7" },
];

export default function Home() {
  const navigate = useNavigate();
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", business: "", wishes: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  // Gender-pref modal that pops the moment a visitor clicks "Build New Automation"
  const [genderOpen, setGenderOpen] = useState(false);

  // ⚠️ TEMPORARY: when DEV_AUTH_BYPASS=1 the marketing landing is
  // skipped and the visitor is taken straight to the admin grid.
  // Mirrors the LoginPage behaviour. Flip DEV_AUTH_BYPASS=0 to undo.
  //
  // SAFETY GUARD (2026-05-23): even when the backend reports the
  // bypass as active, NEVER skip the landing on the production
  // hostname (flow-up.co.il / www.flow-up.co.il). On production we
  // always show the marketing/registration page so visitors aren't
  // dumped into the super-admin grid.
  useEffect(() => {
    let cancelled = false;
    const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
    const isProductionHost =
      host === "flow-up.co.il" || host === "www.flow-up.co.il";
    if (isProductionHost) return; // landing page only, no bypass
    (async () => {
      try {
        const { data: status } = await api.get("/auth/dev-bypass-status");
        if (cancelled || !status?.active) return;
        // If a session already exists we don't need to call dev-bypass.
        let token = null;
        try { token = localStorage.getItem("flowup_token") || sessionStorage.getItem("flowup_token"); } catch { /* ignore */ }
        if (!token) {
          const { data } = await api.post("/auth/dev-bypass");
          if (!data?.token) return;
          try { localStorage.setItem("flowup_token", data.token); } catch { /* ignore */ }
          try { localStorage.setItem("flowup_user", JSON.stringify(data.user)); } catch { /* ignore */ }
        }
        if (cancelled) return;
        navigate("/S.ADMIN", { replace: true });
      } catch { /* bypass disabled — fall through to landing page */ }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const startBuildAutomation = (e) => {
    e?.preventDefault?.();
    if (readGender()) {
      // Already chose — go straight to registration
      navigate("/register");
    } else {
      setGenderOpen(true);
    }
  };

  const updateLead = (k, v) => setLeadForm((p) => ({ ...p, [k]: v }));

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.phone.trim()) {
      toast.error("נא למלא שם וטלפון");
      return;
    }
    setLeadBusy(true);
    try {
      await api.post("/leads", leadForm);
      toast.success("פרטיך התקבלו! ניצור איתך קשר בקרוב 💚");
      setLeadOpen(false);
      setLeadForm({ name: "", phone: "", business: "", wishes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה בשליחה — נסי שוב");
    } finally {
      setLeadBusy(false);
    }
  };

  return (
    <div className="min-h-screen animate-fade-in">
      <Toaster position="top-center" richColors dir="rtl" />

      {/* Top nav — primary CTA: pops the gender modal first, then navigates to /register. */}
      <div className="absolute top-4 left-4 sm:top-5 sm:left-6 lg:left-8 z-30 inline-flex items-center gap-2">
        <Link
          to="/login"
          data-testid="hero-login-btn"
          className="inline-flex items-center gap-1.5 text-sm sm:text-base font-bold bg-white hover:bg-stone-50 text-brand border-2 border-brand px-4 sm:px-5 py-2.5 sm:py-3 rounded-full shadow-md hover:shadow-lg hover:scale-[1.03] transition-all"
        >
          <LogIn className="h-4 w-4" />
          התחברות
        </Link>
        <button
          type="button"
          onClick={startBuildAutomation}
          data-testid="hero-build-automation-btn"
          className="inline-flex items-center gap-2 text-sm sm:text-base font-bold bg-brand hover:bg-brand-hover text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full shadow-lg shadow-brand/40 hover:shadow-xl hover:scale-[1.03] transition-all"
        >
          <Rocket className="h-4 w-4" />
          בנה אוטומציה חדשה
        </button>
      </div>

      <GenderModal
        open={genderOpen}
        onSelect={() => {
          setGenderOpen(false);
          navigate("/register");
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-bl from-brand-50 via-white to-white opacity-70"></div>
        <div
          className="absolute top-0 right-0 h-[500px] w-[500px] rounded-full bg-brand/10 blur-3xl"
          aria-hidden
        ></div>

        {/* Floating logo — small, top-right corner */}
        <img
          src="/logo.png"
          alt="FlowUp"
          className="absolute top-3 right-4 sm:top-4 sm:right-6 lg:right-10 h-14 sm:h-16 lg:h-24 w-auto object-contain pointer-events-none select-none z-30"
          aria-hidden
          data-testid="hero-logo"
        />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-32 pb-12">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-brand/10 text-brand px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5">
              <Sparkles className="h-3.5 w-3.5" />
              FlowUp · ליווי אישי מהתחלה
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.15]">
              אצלנו{" "}
              <span className="text-brand block sm:inline">כל עסק והאוטומציה שלו</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-stone-700 leading-relaxed max-w-2xl mx-auto">
              לכל עסק יש את הדרכים שלו לצמצם עלויות ולמקסם רווחים.
            </p>
            <p className="mt-3 text-base sm:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto">
              לכן הקמנו את <strong className="text-brand">FlowUp</strong> —
              <br />
              רק אצלנו נלווה אתכם מההתחלה ועד אוטומציה מלאה — מותאמת בול לעסק שלכם.
            </p>

            {/* CTA */}
            <div className="mt-8 flex flex-col items-center gap-3">
              <Button
                size="lg"
                className="bg-brand hover:bg-brand-hover text-white font-bold text-base sm:text-lg px-8 py-6 shadow-lg shadow-brand/30 hover:shadow-xl hover:shadow-brand/40 transition-all"
                data-testid="hero-leave-details-cta"
                onClick={() => setLeadOpen(true)}
              >
                <Send className="h-5 w-5 ml-2" />
                לחצי כאן להשאיר פרטים
              </Button>
              {/* "2 דקות" subtitle removed per user request — felt too small/timer-like */}
            </div>

            {/* Stats — directly below CTA, centered */}
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md mx-auto">
              {stats.map((s, idx) => {
                const Icon = s.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-xl border border-border p-3 sm:p-4 text-center"
                    data-testid={`stat-${idx}`}
                  >
                    <Icon className="h-4 w-4 text-brand mx-auto mb-1.5" />
                    <p className="font-heading text-xl sm:text-2xl font-bold text-stone-900">{s.value}</p>
                    <p className="text-[10px] sm:text-xs text-stone-500 mt-0.5">{s.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Lead capture dialog */}
      <Dialog open={leadOpen} onOpenChange={setLeadOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-right text-xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand" />
              נשמח להכיר אתכם
            </DialogTitle>
            <DialogDescription className="text-right text-sm">
              השאירו פרטים וניצור איתכם קשר אישי לתאום שיחה — נבנה יחד אוטומציה שמתאימה בול לעסק שלכם.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitLead} className="space-y-3 py-2" data-testid="lead-form">
            <div>
              <Label>שם <span className="text-rose-500">*</span></Label>
              <Input
                value={leadForm.name}
                onChange={(e) => updateLead("name", e.target.value)}
                placeholder="רחל כהן"
                className="mt-1"
                required
                autoFocus
                data-testid="lead-name-input"
              />
            </div>
            <div>
              <Label>טלפון <span className="text-rose-500">*</span></Label>
              <Input
                type="tel"
                dir="ltr"
                value={leadForm.phone}
                onChange={(e) => updateLead("phone", e.target.value)}
                placeholder="050-1234567"
                className="mt-1 text-right"
                required
                data-testid="lead-phone-input"
              />
            </div>
            <div>
              <Label>מה העסק שלך עושה?</Label>
              <Textarea
                value={leadForm.business}
                onChange={(e) => updateLead("business", e.target.value)}
                placeholder="לדוגמה: סטודיו לעיצוב גרפי לעסקים קטנים"
                rows={2}
                className="mt-1 resize-none"
                data-testid="lead-business-input"
              />
            </div>
            <div>
              <Label>מה הייתם רוצים שיהיה?</Label>
              <Textarea
                value={leadForm.wishes}
                onChange={(e) => updateLead("wishes", e.target.value)}
                placeholder="לדוגמה: שליחת חשבוניות אוטומטית בוואטסאפ ללקוחות קבועים"
                rows={3}
                className="mt-1 resize-none"
                data-testid="lead-wishes-input"
              />
            </div>
            <DialogFooter className="flex-row-reverse gap-2 pt-2">
              <Button
                type="submit"
                disabled={leadBusy}
                className="bg-brand hover:bg-brand-hover text-white font-bold flex-1"
                data-testid="lead-submit-btn"
              >
                {leadBusy ? "שולחת..." : "שלחי פרטים"}
                <ArrowLeft className="h-4 w-4 mr-2" />
              </Button>
              <Button type="button" variant="outline" onClick={() => setLeadOpen(false)}>
                ביטול
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <section id="services-section" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">השירותים שלנו</h2>
          <p className="mt-2 text-stone-600">כל מה שהעסק שלך צריך כדי להתקדם</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {services.map((srv, idx) => {
            const Icon = srv.icon;
            return (
              <div
                key={idx}
                className={`relative rounded-2xl bg-gradient-to-br ${srv.color} border border-stone-200 p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg flex flex-col items-center text-center`}
                data-testid={srv.testid}
              >
                <div className={`h-14 w-14 ${srv.iconBg} rounded-xl flex items-center justify-center shadow-sm mb-4`}>
                  <Icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-4">{srv.category}</h3>
                <ul className="space-y-2.5 w-full">
                  {srv.features.map((f, fi) => (
                    <li key={fi} className="text-center" data-testid={`service-feature-${idx}-${fi}`}>
                      <button
                        type="button"
                        className="w-full text-sm font-medium text-stone-800 hover:text-brand transition cursor-pointer py-1"
                      >
                        {f.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
